import React, {useEffect, useState} from "react";
import {useLazyQuery} from "@apollo/client";
import CAMPER_OPTIONS from "../../../graphql/queries/camperOptions";
import CAMPER_PICKUP_LOCATIONS from "../../../graphql/queries/camperPickupLocations";
import {useRouter} from "next/router";
import moment from "moment";
import dynamic from "next/dynamic";
import {Col, DropdownItem, FormGroup, Row} from "reactstrap";
import Select from "react-select";
import AddCamperToCartButton from "../../Button/AddCamperToCartButton";

let DateRangePicker = dynamic(
    () => import("./DateRangePicker"),
    {ssr: false}
);

interface SelectionFormProps {
    setOptions: (options) => void
    setLoading: (value: boolean) => void
    searching: boolean
    options: any
    totalPrice: number
    camperBasePrice: number
    selectedOptions: any
    selectedInsurance: any
    camperInfo: any
}

interface TripSelection {
    camperSeoId: string
    pickupLocation?: string
    pickupDateTime?: moment.Moment
    dropOffLocation?: string
    dropOffDateTime?: moment.Moment
}

const queryToTripSelection = (queries): TripSelection => {
    return {
        camperSeoId: queries.index,
        pickupLocation: queries.ophaallocatie,
        pickupDateTime: queries.ophaaldatum ? moment(queries.ophaaldatum) : null,
        dropOffLocation: queries.afleverlocatie,
        dropOffDateTime: queries.afleverdatum ? moment(queries.afleverdatum) : null
    }
};

const isTripSelectionComplete = (tripSelection: TripSelection): boolean => {
    return tripSelection.camperSeoId
        && tripSelection.pickupLocation
        && (tripSelection.pickupDateTime !== null)
        && tripSelection.dropOffLocation
        && (tripSelection.dropOffDateTime !== null)
        ;
}

const getTripLengthInDays = (startDate: moment.Moment, endDate: moment.Moment): number => {
    return moment(endDate).diff(moment(startDate), 'days') + 1;
}

const AddToCartButtonComponent = React.forwardRef((props: any, ref) => (
    <AddCamperToCartButton {...props} />
));

const encodeBookingDetails = (details) => {
    return Buffer.from(JSON.stringify(details)).toString("base64");
};

const SelectionForm = ({
                           setOptions,
                           options,
                           totalPrice,
                           selectedOptions,
                           camperInfo,
                           selectedInsurance,
                           camperBasePrice,
                           setLoading,
                           searching
                       }: SelectionFormProps) => {
    const router = useRouter();
    const [tripSelection, setTripSelection] = useState(queryToTripSelection(router.query));
    const [pickupLocations, setPickupLocations] = useState([]);

    const [getOptions, {loading}] = useLazyQuery(CAMPER_OPTIONS, {
        fetchPolicy: 'no-cache',
        onCompleted: (data) => {
            setOptions(data.CamperOptions);
            setLoading(false);
        },
    });

    const [getPickupLocations] = useLazyQuery(CAMPER_PICKUP_LOCATIONS, {
        fetchPolicy: 'no-cache',
        onCompleted: (data) => setPickupLocations(data.CamperPickupLocations.locations.map(location => {
            return {
                label: location.location,
                value: location.id,
                country: location.seoId,
            }
        }))
    });

    useEffect(() => {
        getPickupLocations();
    }, [])

    useEffect(() => {
        const tripSelection = queryToTripSelection(router.query);
        setTripSelection(tripSelection);

        if (isTripSelectionComplete(tripSelection)) {
            loadCamperOptions(tripSelection);
        }
    }, [router.query])

    const loadCamperOptions = (tripSelection: TripSelection) => {
        setLoading(true);
        getOptions({
            variables: {
                camperSeoId: tripSelection.camperSeoId,
                pickupCity: tripSelection.pickupLocation,
                pickupDateTime: tripSelection.pickupDateTime.format('YYYY-MM-DD 11:00'),
                dropOffCity: tripSelection.dropOffLocation,
                dropOffDateTime: tripSelection.dropOffDateTime.format('YYYY-MM-DD 11:00')
            }
        });
    }

    const updateDateRange = ({startDate, endDate}) => {
        let urlQuery = {
            'ophaaldatum': startDate.format('YYYY-MM-DD'),
            'afleverdatum': endDate.format('YYYY-MM-DD'),
            'ophaallocatie': tripSelection.pickupLocation ?? undefined,
            'afleverlocatie': tripSelection.dropOffLocation ?? undefined,
        };

        router.replace(
            {
                pathname: "/camper/type/[index]",
                query: urlQuery,
            },
            {
                pathname: `/camper/type/${router.query.index}`,
                query: urlQuery,
            },
            {
                shallow: true,
            }
        );
    };

    const changePickUpLocation = (location) => {
        let urlQuery = {
            'ophaaldatum': tripSelection.pickupDateTime ? tripSelection.pickupDateTime.format('YYYY-MM-DD') : undefined,
            'afleverdatum': tripSelection.dropOffDateTime ? tripSelection.dropOffDateTime.format('YYYY-MM-DD') : undefined,
            'ophaallocatie': location.label,
            'afleverlocatie': tripSelection.dropOffLocation ?? undefined,
        };

        router.replace(
            {
                pathname: "/camper/type/[index]",
                query: urlQuery,
            },
            {
                pathname: `/camper/type/${router.query.index}`,
                query: urlQuery,
            },
            {
                shallow: true,
            }
        );
    }

    const changeDropOffLocation = (location) => {
        let urlQuery = {
            'ophaaldatum': tripSelection.pickupDateTime ? tripSelection.pickupDateTime.format('YYYY-MM-DD') : undefined,
            'afleverdatum': tripSelection.dropOffDateTime ? tripSelection.dropOffDateTime.format('YYYY-MM-DD') : undefined,
            'ophaallocatie': tripSelection.pickupLocation ?? undefined,
            'afleverlocatie': location.label,
        };

        router.replace(
            {
                pathname: "/camper/type/[index]",
                query: urlQuery,
            },
            {
                pathname: `/camper/type/${router.query.index}`,
                query: urlQuery,
            },
            {
                shallow: true,
            }
        );
    }

    return (
        <>
            <h2>Selecteer uw trip</h2>

            <DateRangePicker
                pickupDate={tripSelection.pickupDateTime}
                dropOffDate={tripSelection.dropOffDateTime}
                updateDates={updateDateRange}
            />

            <Row>
                <Col>
                    <h6 className="mt-4">Ophaal locatie</h6>
                    <DropdownItem toggle={false} className="pl-0 pr-0">
                        <Select
                            value={tripSelection.pickupLocation ? pickupLocations.find(location => location.label == tripSelection.pickupLocation) : null}
                            instanceId={`pickupLocationPicker`}
                            options={pickupLocations}
                            placeholder="Kies een afhaallocatie"
                            className="selectpicker"
                            classNamePrefix="selectpicker"
                            onChange={(location) => {
                                changePickUpLocation(location)
                            }}
                        />
                    </DropdownItem>
                </Col>
                <Col>
                    <h6 className="mt-4">Aflever locatie</h6>
                    <DropdownItem toggle={false} className="pl-0 pr-0">
                        <Select
                            value={tripSelection.dropOffLocation ? pickupLocations.find(location => location.label == tripSelection.dropOffLocation) : null}
                            instanceId={`pickupLocationPicker`}
                            options={pickupLocations}
                            placeholder="Kies een afleverlocatie"
                            className="selectpicker"
                            classNamePrefix="selectpicker"
                            onChange={(location) => {
                                changeDropOffLocation(location)
                            }}
                        />
                    </DropdownItem>
                </Col>
            </Row>
            {camperBasePrice && !searching ?
                <Row className="mt-4 text-sm">
                    {options &&
                        <Col>
                            <h6>Prijs</h6>
                            <Row>
                                <Col lg="8">
                                    camper
                                    voor {getTripLengthInDays(tripSelection.pickupDateTime, tripSelection.dropOffDateTime)} dagen
                                </Col>
                                <Col lg="4">
                                    <span className="float-right">&euro; {camperBasePrice}</span>
                                </Col>
                                <Col lg="8">
                                    {selectedInsurance && selectedInsurance.name}
                                </Col>
                                <Col lg="4">
                                    <span
                                        className="float-right">&euro; {selectedInsurance && selectedInsurance.totalAmountEuro}</span>
                                </Col>
                                {selectedOptions.map((selectedOption, index) => (
                                    <React.Fragment key={index}>
                                        <Col lg="8" className="text-sm">{selectedOption.name}</Col>
                                        <Col lg="4">
                                            <span className="float-right">&euro; {selectedOption.totalFeeEuro}</span>
                                        </Col>
                                    </React.Fragment>
                                ))}
                            </Row>
                            <hr className="my-4"/>
                            <Row>
                                <Col lg="8">
                                    <span className="h6">Totaal</span>
                                </Col>
                                <Col lg="4">
                                    <span className="h6 float-right">&euro; {totalPrice}</span>
                                </Col>
                            </Row>
                        </Col>
                    }
                </Row> :
                <>
                    {searching ?
                        <Row className="mt-4 text-sm">
                            <Col>
                                Beschikbaarheid aan het ophalen
                            </Col>
                        </Row>
                        :
                        <Row className="mt-4 text-sm">
                            <Col>
                                Deze camper is niet beschikbaar voor deze data en afhaallocaties
                            </Col>
                        </Row>
                    }
                </>
            }

            {camperBasePrice > 0 && !searching &&
                <p>
                    <AddToCartButtonComponent
                        camperSeoId={router.query.index}
                        pickupDate={tripSelection.pickupDateTime}
                        dropOffDate={tripSelection.dropOffDateTime}
                        pickupLocation={tripSelection.pickupLocation}
                        dropOffLocation={tripSelection.dropOffLocation}
                        insuranceOptions={JSON.stringify(selectedInsurance)}
                        options={JSON.stringify(selectedOptions)}
                    />
                </p>
            }

            {camperBasePrice > 0 && !searching &&
                <FormGroup>
                    {/*<Link*/}
                    {/*    href={`/camper/boeken/[details]`}*/}
                    {/*    as={`/camper/boeken/${encodeBookingDetails({*/}
                    {/*        camper: camperInfo ? camperInfo.type : '',*/}
                    {/*        startDate: tripSelection.pickupDateTime ? tripSelection.pickupDateTime.format('YYYY-MM-DD') : null,*/}
                    {/*        endDate: tripSelection.dropOffDateTime ? tripSelection.dropOffDateTime.format('YYYY-MM-DD') : null,*/}
                    {/*        thumbnail: camperInfo ? camperInfo.images[0] : '',*/}
                    {/*        optionalFees: selectedOptions,*/}
                    {/*        insurance: selectedInsurance,*/}
                    {/*        camperBasePrice: camperBasePrice,*/}
                    {/*        tripLength: getTripLengthInDays(tripSelection.pickupDateTime, tripSelection.dropOffDateTime),*/}
                    {/*        totalPrice: totalPrice*/}
                    {/*    })}`}*/}
                    {/*    passHref*/}
                    {/*>*/}
                    {/*    <Button*/}
                    {/*        aria-label="Boek cottage"*/}
                    {/*        className="book-button"*/}
                    {/*        color="primary"*/}
                    {/*        block*/}
                    {/*    >*/}
                    {/*        Boek uw camper*/}
                    {/*    </Button>*/}
                    {/*</Link>*/}
                    <p className="text-sm text-center no-charges-text">
                        Er wordt nog niets in rekening gebracht
                    </p>
                </FormGroup>
            }
        </>
    )
}

export default SelectionForm;
